import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Minus, Receipt, Camera, Eye, Download, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getErrorMessage = (error, defaultMsg) => {
  return error.response?.data?.detail || error.message || defaultMsg;
};

const PettyCashManagement = () => {
  const { activeBranch } = useBranch();
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Date filter states
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Receipt upload states
  const [uploadReceiptDialog, setUploadReceiptDialog] = useState(false);
  const [selectedTransactionForReceipt, setSelectedTransactionForReceipt] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  
  // Dialogs
  const [showAddCashDialog, setShowAddCashDialog] = useState(false);
  const [showAddExpenseDialog, setShowAddExpenseDialog] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState(null);
  
  // Forms
  const [cashForm, setCashForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'PT Cash Add',
    amount: '',
    description: ''
  });
  
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    amount: '',
    description: '',
    receipt: null
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (activeBranch) {
      fetchData();
    }
  }, [activeBranch]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/petty-cash/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
      if (response.data.categories?.length > 0) {
        setExpenseForm(prev => ({ ...prev, category: response.data.categories[0] }));
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const fetchData = async () => {
    if (!activeBranch) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params with date filters
      let summaryParams = `branch_id=${activeBranch.branch_id}`;
      let transactionsParams = `branch_id=${activeBranch.branch_id}`;
      
      if (fromDate) {
        summaryParams += `&from_date=${fromDate}`;
        transactionsParams += `&from_date=${fromDate}`;
      }
      if (toDate) {
        summaryParams += `&to_date=${toDate}`;
        transactionsParams += `&to_date=${toDate}`;
      }
      
      // Fetch summary and transactions in parallel
      const [summaryRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/api/petty-cash/summary?${summaryParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/petty-cash/transactions?${transactionsParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load petty cash data'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCash = async (e) => {
    e.preventDefault();
    if (!cashForm.amount || parseFloat(cashForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/petty-cash/add-cash`, {
        branch_id: activeBranch.branch_id,
        date: cashForm.date,
        category: cashForm.category,
        amount: parseFloat(cashForm.amount),
        description: cashForm.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Cash added successfully!');
      setShowAddCashDialog(false);
      setCashForm({
        date: new Date().toISOString().split('T')[0],
        category: 'PT Cash Add',
        amount: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add cash'));
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('branch_id', activeBranch.branch_id);
      formData.append('date', expenseForm.date);
      formData.append('category', expenseForm.category);
      formData.append('amount', expenseForm.amount);
      if (expenseForm.description) {
        formData.append('description', expenseForm.description);
      }
      if (expenseForm.receipt) {
        formData.append('receipt', expenseForm.receipt);
      }

      await axios.post(`${API}/api/petty-cash/add-expense`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Expense added successfully!');
      setShowAddExpenseDialog(false);
      setExpenseForm({
        date: new Date().toISOString().split('T')[0],
        category: categories[0] || '',
        amount: '',
        description: '',
        receipt: null
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add expense'));
    }
  };

  const viewReceipt = (transaction) => {
    if (transaction.receipt_url) {
      setCurrentReceipt(transaction);
      setShowReceiptModal(true);
    }
  };

  const downloadReceipt = () => {
    if (currentReceipt?.receipt_url) {
      window.open(currentReceipt.receipt_url, '_blank');
    }
  };

  const handleUploadReceipt = async (e) => {
    e.preventDefault();
    if (!receiptFile || !selectedTransactionForReceipt) return;

    setUploadingReceipt(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('receipt', receiptFile);

      await axios.post(
        `${API}/api/petty-cash/transaction/${selectedTransactionForReceipt.transaction_id}/receipt`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Receipt uploaded successfully!');
      setUploadReceiptDialog(false);
      setReceiptFile(null);
      setSelectedTransactionForReceipt(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload receipt'));
    } finally {
      setUploadingReceipt(false);
    }
  };


  if (loading && !summary) {
    return (
      <Layout title="Petty Cash Management">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
            <p className="mt-4 text-[#2C4C3B]">Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Petty Cash & Expense Management">
      <div className="space-y-6">
        {/* Date Filter */}
        <Card className="p-4 bg-white border-[#E5E5DF]">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm font-medium text-[#2C4C3B]">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm font-medium text-[#2C4C3B]">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={fetchData}
                className="bg-[#2C4C3B] hover:bg-[#1e3329]"
              >
                Apply Filter
              </Button>
              {(fromDate || toDate) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFromDate('');
                    setToDate('');
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="text-sm text-green-700 mb-2">Current Balance</div>
              <div className={`text-3xl font-bold ${summary.current_balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                ₹{summary.current_balance.toLocaleString()}
              </div>
            </Card>
            
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="text-sm text-blue-700 mb-2">Total Cash Added</div>
              <div className="text-3xl font-bold text-blue-700">
                ₹{summary.total_cash_added.toLocaleString()}
              </div>
            </Card>
            
            <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <div className="text-sm text-orange-700 mb-2">Total Expenses</div>
              <div className="text-3xl font-bold text-orange-700">
                ₹{summary.total_expenses.toLocaleString()}
              </div>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex gap-4">
          <Button onClick={() => setShowAddCashDialog(true)} className="bg-green-600 hover:bg-green-700">
            <Plus size={20} className="mr-2" />
            Add Cash
          </Button>
          <Button onClick={() => setShowAddExpenseDialog(true)} className="bg-orange-600 hover:bg-orange-700">
            <Minus size={20} className="mr-2" />
            Add Expense
          </Button>
        </div>

        {/* Transaction History */}
        <Card>
          <div className="p-6">
            <h3 className="font-semibold text-lg mb-4">Transaction History</h3>
            
            {transactions.length === 0 ? (
              <p className="text-center text-[#1C1C1A]/60 py-8">No transactions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#E5E5DF]">
                    <tr>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-left">Type</th>
                      <th className="p-3 text-left">Category</th>
                      <th className="p-3 text-left">Description</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-center">Receipt</th>
                      <th className="p-3 text-left">Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn) => (
                      <tr key={txn.transaction_id} className="border-b border-[#E5E5DF] hover:bg-[#F5F5F0]">
                        <td className="p-3">{txn.date}</td>
                        <td className="p-3">
                          <Badge variant={txn.type === 'income' ? 'success' : 'destructive'}>
                            {txn.type === 'income' ? '+ Income' : '- Expense'}
                          </Badge>
                        </td>
                        <td className="p-3">{txn.category}</td>
                        <td className="p-3 text-[#1C1C1A]/70">{txn.description || '-'}</td>
                        <td className="p-3 text-right">
                          <span className={`font-medium ${txn.type === 'income' ? 'text-green-700' : 'text-red-700'}`}>
                            {txn.type === 'income' ? '+' : '-'}₹{txn.amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {txn.receipt_url ? (
                            <Button size="sm" variant="outline" onClick={() => viewReceipt(txn)}>
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                          ) : txn.type === 'expense' ? (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setSelectedTransactionForReceipt(txn);
                                setUploadReceiptDialog(true);
                              }}
                            >
                              <Camera size={16} className="mr-1" />
                              Upload
                            </Button>
                          ) : (
                            <span className="text-[#1C1C1A]/40">-</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-[#1C1C1A]/60">{txn.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        {/* Add Cash Dialog */}
        <Dialog open={showAddCashDialog} onOpenChange={setShowAddCashDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add Cash</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddCash} className="space-y-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  required
                  value={cashForm.date}
                  onChange={(e) => setCashForm({...cashForm, date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Category / Note *</Label>
                <Input
                  required
                  value={cashForm.category}
                  onChange={(e) => setCashForm({...cashForm, category: e.target.value})}
                  placeholder="e.g., PT Cash Add"
                />
              </div>
              
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  value={cashForm.amount}
                  onChange={(e) => setCashForm({...cashForm, amount: e.target.value})}
                  placeholder="Enter amount"
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Input
                  value={cashForm.description}
                  onChange={(e) => setCashForm({...cashForm, description: e.target.value})}
                  placeholder="Additional notes"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddCashDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                  Add Cash
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Expense Dialog */}
        <Dialog open={showAddExpenseDialog} onOpenChange={setShowAddExpenseDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  required
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select value={expenseForm.category} onValueChange={(val) => setExpenseForm({...expenseForm, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                  placeholder="Enter amount"
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Input
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  placeholder="Additional notes"
                />
              </div>
              
              <div>
                <Label>Upload Receipt (Optional)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setExpenseForm({...expenseForm, receipt: e.target.files[0]})}
                    className="block w-full text-sm text-[#1C1C1A]/70 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#E5E5DF] file:text-[#2C4C3B] hover:file:bg-[#D5D5CF]"
                  />
                  {expenseForm.receipt && (
                    <p className="mt-2 text-sm text-green-700">
                      <Camera size={16} className="inline mr-1" />
                      {expenseForm.receipt.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddExpenseDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                  Add Expense
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Receipt Viewer Modal */}
        <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Receipt</DialogTitle>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={downloadReceipt}>
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
              </div>
            </DialogHeader>
            {currentReceipt && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#1C1C1A]/60">Date:</span>
                    <span className="ml-2 font-medium">{currentReceipt.date}</span>
                  </div>
                  <div>
                    <span className="text-[#1C1C1A]/60">Category:</span>
                    <span className="ml-2 font-medium">{currentReceipt.category}</span>
                  </div>
                  <div>
                    <span className="text-[#1C1C1A]/60">Amount:</span>
                    <span className="ml-2 font-medium text-red-700">₹{currentReceipt.amount.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-[#1C1C1A]/60">Added By:</span>
                    <span className="ml-2 font-medium">{currentReceipt.created_by_name}</span>
                  </div>
                </div>
                
                {currentReceipt.description && (
                  <div className="text-sm">
                    <span className="text-[#1C1C1A]/60">Description:</span>
                    <p className="mt-1">{currentReceipt.description}</p>
                  </div>
                )}
                
                <div className="border rounded-lg overflow-hidden">
                  <img 
                    src={currentReceipt.receipt_url} 
                    alt="Receipt" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Upload Receipt Dialog */}
        <Dialog open={uploadReceiptDialog} onOpenChange={setUploadReceiptDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Upload Receipt</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUploadReceipt} className="space-y-4">
              {selectedTransactionForReceipt && (
                <div className="p-3 bg-[#F5F5F0] rounded-lg space-y-2 text-sm">
                  <div>
                    <span className="text-[#1C1C1A]/60">Date:</span>
                    <span className="ml-2 font-medium">{selectedTransactionForReceipt.date}</span>
                  </div>
                  <div>
                    <span className="text-[#1C1C1A]/60">Category:</span>
                    <span className="ml-2 font-medium">{selectedTransactionForReceipt.category}</span>
                  </div>
                  <div>
                    <span className="text-[#1C1C1A]/60">Amount:</span>
                    <span className="ml-2 font-medium text-red-700">-₹{selectedTransactionForReceipt.amount.toLocaleString()}</span>
                  </div>
                </div>
              )}
              
              <div>
                <Label>Receipt Image *</Label>
                <div className="mt-2">
                  <Input
                    type="file"
                    accept="image/*"
                    required
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                  />
                </div>
                {receiptFile && (
                  <p className="text-xs text-[#1C1C1A]/60 mt-2">
                    Selected: {receiptFile.name}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUploadReceiptDialog(false);
                    setReceiptFile(null);
                  }}
                  className="flex-1"
                  disabled={uploadingReceipt}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#2C4C3B] hover:bg-[#1e3329]"
                  disabled={uploadingReceipt || !receiptFile}
                >
                  {uploadingReceipt ? 'Uploading...' : 'Upload Receipt'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PettyCashManagement;
